/**
 * Canonical document HTML renderer.
 * Single source of truth for Print, PDF, and Email document content.
 * All three outputs MUST use this so layout and values always match.
 */
import { renderTemplate } from './templateRenderer';
import { assembleDocument } from './assembleDocument';
import { getEnableDueDates } from '../services/settings';
import { PRINT_DOCUMENT_CSS } from './printDocumentStyles';
import { supabase } from '../lib/supabase';

/** HTML comment injected so tests can assert email/print use canonical renderer (not saved template) */
export const CANONICAL_RENDERER_SIGNATURE = '<!-- rendered by canonical document renderer -->';

/**
 * Renders a document (invoice/estimate) to HTML using the same pipeline as Print and PDF.
 * @param {object} document - Invoice or estimate (items, subtotal, total, date, etc.)
 * @param {object} client - Client (name, address, city, state, zip, email, phone)
 * @param {object} letterTemplate - Letter template { header, body, footer }
 * @param {{ type?: 'invoice' | 'estimate', hideTotals?: boolean, signature?: boolean }} options
 * @returns {Promise<string>} Full document HTML (same as print view)
 */
export async function renderDocumentHtml(document, client, letterTemplate, options = {}) {
  const type = options.type ?? (document.type === 'estimate' ? 'estimate' : 'invoice');
  const hideTotals = options.hideTotals ?? document.hide_totals ?? false;
  const includeSignature = options.signature !== false;

  const clientCityStateZip = [client.city, client.state, client.zip].filter(Boolean).join(', ') || '';
  const invoiceDate = document.date ? new Date(document.date).toLocaleDateString() : '';
  const invoiceNumber = document.invoice_number || document.id;
  const items = typeof document.items === 'string' ? JSON.parse(document.items || '[]') : (document.items || []);

  let enableDueDates = true;
  try {
    enableDueDates = await getEnableDueDates();
  } catch {
    // default enabled
  }
  const dueDate = enableDueDates && document.due_date
    ? new Date(document.due_date).toLocaleDateString()
    : '';

  let userName = '';
  try {
    const { data: { user: authUser } } = await supabase.auth.getUser();
    userName = authUser?.user_metadata?.full_name || authUser?.email || '';
  } catch {
    // User not available, leave empty
  }

  const templateData = {
    document_type: type === 'estimate' ? 'ESTIMATE' : 'INVOICE',
    client: {
      name: client.name || 'Client',
      address: client.address || '',
      city_state_zip: clientCityStateZip,
      email: client.email || '',
      phone: client.phone || '',
    },
    invoice: {
      number: invoiceNumber,
      date: invoiceDate,
      due_date: dueDate,
      document_type: type === 'estimate' ? 'ESTIMATE' : 'INVOICE',
      subtotal: `$${parseFloat(document.subtotal || 0).toFixed(2)}`,
      tax: `$${parseFloat(document.tax_amount || 0).toFixed(2)}`,
      tax_amount: `$${parseFloat(document.tax_amount || 0).toFixed(2)}`,
      total: `$${parseFloat(document.total || 0).toFixed(2)}`,
      tax_rate: document.tax_rate || 0,
      include_tax: document.include_tax !== false,
      taxable_subtotal: document.taxable_subtotal || 0,
      non_taxable_subtotal: document.non_taxable_subtotal || 0,
      line_items: items,
      items_table: items,
      notes: document.notes || '',
    },
    date: invoiceDate,
    due_date: dueDate,
    facility: {
      name: 'Menlo Atherton Glass',
      address: 'PO Box 5214, Redwood City CA 94063',
    },
    user: {
      name: userName,
    },
    notes_section: document.notes || '',
    tax_rate: document.tax_rate || 0,
  };

  const assembledHTML = assembleDocument({
    header: letterTemplate.header || '',
    body: letterTemplate.body || '',
    footer: letterTemplate.footer || '',
  });

  const html = renderTemplate(assembledHTML, templateData, hideTotals);
  return includeSignature ? `${CANONICAL_RENDERER_SIGNATURE}\n${html}` : html;
}

/**
 * Build full document HTML and CSS for Print or PDF/Email.
 * Single source of truth: same HTML as renderDocumentHtml, plus canonical print CSS for inlining.
 * Use for PDF generation so Email PDF matches Print (inline CSS = deterministic, no missing assets).
 *
 * @param {object} document - Invoice or estimate
 * @param {object} client - Client record
 * @param {object} letterTemplate - Letter template { header, body, footer }
 * @param {{ mode?: 'print' | 'email', type?: 'invoice' | 'estimate', hideTotals?: boolean }} options
 * @returns {Promise<{ html: string, css: string }>}
 */
async function buildInvoiceHtml(document, client, letterTemplate, options = {}) {
  const html = await renderDocumentHtml(document, client, letterTemplate, {
    type: options.type ?? (document.type === 'estimate' ? 'estimate' : 'invoice'),
    hideTotals: options.hideTotals ?? document.hide_totals ?? false,
    signature: true,
  });
  return { html, css: PRINT_DOCUMENT_CSS };
}

export { buildInvoiceHtml };
